<template lang="pug">
  AuthorDialog(
    :open="toValue(open)"
    @close="closeDialog"
    class="w-full max-w-[90vw] lg_max-w-5xl bg-slate-50 dark_bg-gray-900 p-4 sm_p-6"
  )
    form.w-full.z-10.flex.flex-col.gap-5
      .flex.items-center.gap-3
        .rounded-full.size-8.flex.items-center.justify-center.shrink-0(
          v-html="logoClosedVwAi"
          aria-hidden="true"
          class="w-8 text-indigo-600 dark_text-indigo-400 text-base"
        )
        h3.text-gray-900.dark_text-gray-100.font-medium.leading-tight(class="text-xl") Chat files

      p.text-gray-800.dark_text-gray-200.leading-6
        strong.font-semibold Shared document library.
        |  Files uploaded here are available to everyone on your organisation's account. Use this for policies, style guides, source material, or anything the team wants the agent to draw on.
      p.text-gray-800.dark_text-gray-200.leading-6
        strong.font-semibold Using them in chat:
        |  click 
        em Use in chat
        |  next to a document, or mention it by name in your message, and I'll reference it or pull content directly into your course.
      p.text-xs.italic.text-gray-500.dark_text-gray-400.leading-5
        | Files are shared within your organisation only. They are never used for external model training.

      .border.border-dashed.border-slate-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800.p-4(
        ref="dropzoneTarget"
      )
        input.hidden(type="file" id="file" accept=".pdf,.txt,.md" @change="onFileChange")
        .flex.items-center.justify-center.gap-4.text-center.py-2(class="flex-col sm_flex-row")
          .rounded-full.size-10.bg-indigo-100.dark_bg-gray-700.flex.items-center.justify-center.shrink-0
            Icon(icon="ph:file-arrow-up-fill" class="text-indigo-600 dark_text-indigo-400 text-lg")
          .flex.flex-col.items-start
            p.text-sm.font-semibold.text-gray-900.dark_text-gray-100
              | Drag & drop here or
              label.text-indigo-600.dark_text-indigo-400.cursor-pointer.ml-1(for="file") browse...
            p.text-xs.text-gray-500.dark_text-gray-400.leading-5.text-left
              | Supported formats: PDF, TXT, MD
              br
              | Max file size: 10MB

        .mt-2.px-4.py-3.border.border-slate-300.dark_border-gray-600.rounded-xl.bg-white.dark_bg-gray-800.flex.items-center.gap-3(
          v-if="uploadDisplayName"
        )
          Icon.shrink-0(
            :icon="submitStatus === 'pending' ? 'ph:spinner-gap' : 'ph:file'"
            :class="submitStatus === 'pending' ? 'text-fuchsia-500 animate-spin text-lg' : 'text-indigo-500 text-lg'"
          )
          .min-w-0.flex.flex-col
            p.text-sm.font-medium.text-gray-900.dark_text-gray-100.truncate
              | File {{ uploadDisplayName }} {{ uploadDisplayFormat }}
            p.text-xs(
              v-if="submitStatus === 'pending'"
              class="text-gray-500 dark_text-gray-400"
            ) Uploading...
            p.text-xs(
              v-else-if="submitStatus === 'success'"
              class="text-green-600 dark_text-green-400"
            ) Uploaded successfully
            p.text-xs(
              v-else-if="submitStatus === 'error'"
              class="text-red-600 dark_text-red-400"
            ) Failed to upload file. Please ensure you have the correct file type and your network settings allow access to this site.
          button.btn.btn-sm.btn-outline.bg-transparent.hover_bg-indigo-50.dark_hover_bg-gray-700.flex.items-center.gap-1.px-2.h-7.ml-auto.shrink-0(
            v-if="submitStatus === 'success' && uploadDisplayName"
            @click="useDocumentInChat(uploadDisplayName)"
            title="Use this document in chat"
            type="button"
          )
            Icon.text-base.text-indigo-600.dark_text-indigo-400(icon="ph:paperclip")
            span.text-xs.text-gray-700.dark_text-gray-200 Use in chat

      .flex.flex-col.gap-2
        p.text-gray-900.dark_text-gray-100.leading-6 Uploaded files
        .flex.flex-col.gap-2.overflow-y-auto.pr-1(class="max-h-[40vh] sm_max-h-[34vh]")
          .text-center.text-gray-500.dark_text-gray-400.py-4.border.border-slate-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800(
            v-if="!allDocuments.length"
          ) No documents uploaded yet

          .flex.items-center.justify-between.pl-4.pr-2.py-3.border.border-slate-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800(
            v-for="doc in allDocuments"
            :key="doc.id"
          )
            .flex.items-center.gap-3.min-w-0.flex-1
              Icon(icon="ph:file" class="text-indigo-600 dark_text-indigo-400 text-xl shrink-0")
              p.text-gray-900.dark_text-gray-100.text-sm.font-medium.truncate
                | {{ doc.originalFileName }}
              p.hidden.md_block.text-gray-900.dark_text-gray-100.text-sm.font-medium.truncate.pl-3.border-l.border-slate-300.dark_border-gray-600
                | {{ doc.title }}
            .flex.items-center.gap-2.pl-3.border-l.border-slate-300.dark_border-gray-600
              button.btn.btn-sm.btn-outline.bg-transparent.hover_bg-indigo-50.dark_hover_bg-gray-700.flex.items-center.gap-1.px-2.h-7(
                @click="useDocumentInChat(documentTitle(doc))"
                title="Use this document in chat"
                type="button"
              )
                Icon.text-base.text-indigo-600.dark_text-indigo-400(icon="ph:paperclip")
                span.text-xs.text-gray-700.dark_text-gray-200 Use in chat
              button.btn.btn-sm.btn-outline.bg-transparent.hover_bg-gray-400.p-0.flex.items-center.justify-center.size-6(
                @click="openDeleteConfirm(doc.id)"
                title="Delete document"
                type="button"
              )
                span.sr-only Delete
                Icon.inline-block.text-base.text-gray-500.dark_text-gray-400(icon="ph:trash-simple")

    AuthorDialog(
      :open="isDeleteConfirmOpen"
      @close="cancelDeleteConfirm"
      class="w-full max-w-md bg-slate-50 dark_bg-gray-900 p-4 sm_p-6"
    )
      .flex.flex-col.gap-4
        .flex.items-center.gap-3
          .rounded-full.size-8.bg-red-100.dark_bg-red-900.flex.items-center.justify-center.shrink-0
            Icon(icon="ph:warning-circle-fill" class="text-red-600 dark_text-red-300 text-base")
          h3.text-gray-900.dark_text-gray-100.font-medium.leading-tight(class="text-lg") Delete document
        p.text-sm.text-gray-700.dark_text-gray-300.leading-6
          | Are you sure you want to delete this document? This action cannot be undone.
        .flex.justify-end.gap-2.mt-1
          button.btn.btn-sm.btn-outline.bg-transparent(
            @click="cancelDeleteConfirm"
            type="button"
          ) Cancel
          button.btn.btn-sm.bg-red-600.hover_bg-red-700.text-white(
            @click="confirmDeleteDocument"
            type="button"
          ) Delete
</template>
<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
  toValue
} from 'vue';
import AuthorDialog from './AuthorDialog.vue';
import { EDITAGENT_API_BASE } from '../lib/editAgentApi';
import callClientPortal from '../../lib/callClientPortal';
import { Icon } from '@iconify/vue';
import Dropzone from 'dropzone';
import 'dropzone/dist/dropzone.css';
import logoClosedVwAi from '../icons/logo-closed-VW-ai.svg?raw';
const emit = defineEmits(['close', 'success', 'use-document']);

interface Document {
  id: string;
  size: number;
  url?: string;
  originalFileName?: string;
  mimeType?: string;
  title?: string;
}

const submitStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle');
const allDocuments = ref<Document[]>([]);
const file = ref<File | null>(null);
const uploadDisplayName = ref('');
const dropzoneTarget = ref<HTMLElement | null>(null);
const dz = ref<Dropzone | null>(null);
const pendingDeleteDocumentId = ref<string | null>(null);
const isDeleteConfirmOpen = computed(
  () => pendingDeleteDocumentId.value !== null
);

const props = defineProps<{
  open: boolean;
}>();

const uploadDisplayFormat = computed(() => {
  if (!uploadDisplayName.value.includes('.')) return 'FILE';
  const extension = uploadDisplayName.value.split('.').pop();
  return extension ? extension.toUpperCase() : 'FILE';
});

const findFileExtension = (mimeType: string): string => {
  switch (mimeType) {
    case 'text/markdown':
      return 'markdown';
    case 'text/html':
      return 'html';
    case 'text/plain':
      return 'txt';
    case 'application/pdf':
      return 'pdf';
    case 'application/msword':
      return 'doc';
    default:
      return 'unknown';
  }
};

const documentTitle = (doc: Document): string =>
  doc.originalFileName || doc.title || doc.id;

const useDocumentInChat = (title: string) => {
  if (!title) return;
  emit('use-document', title);
  closeDialog();
};

const getDocuments = async () => {
  const { response } = await callClientPortal(
    '/documents',
    {
      method: 'GET'
    },
    { base: EDITAGENT_API_BASE }
  );
  allDocuments.value = response.data;
};

onMounted(async () => {
  await getDocuments();
});

const setSelectedFile = (selectedFile: File) => {
  if (submitStatus.value === 'pending') return;
  file.value = selectedFile;
  uploadDisplayName.value = selectedFile.name;
  submitStatus.value = 'idle';
  void uploadSelectedFile(selectedFile);
};

const clearSelectedFile = (clearDisplay = false) => {
  file.value = null;
  if (clearDisplay) {
    uploadDisplayName.value = '';
  }
  const fileInput = document.getElementById('file') as HTMLInputElement | null;
  if (fileInput) {
    fileInput.value = '';
  }
  dz.value?.removeAllFiles(true);
};

const closeDialog = () => {
  clearSelectedFile(true);
  submitStatus.value = 'idle';
  dz.value?.destroy();
  dz.value = null;
  emit('close');
};

const initDropzone = () => {
  if (!dropzoneTarget.value || dz.value) return;
  dz.value = new Dropzone(dropzoneTarget.value, {
    url: '/documents/upload',
    autoProcessQueue: false,
    clickable: false,
    previewsContainer: false,
    createImageThumbnails: false,
    acceptedFiles: '.pdf,.txt,.md',
    maxFilesize: 10,
    maxFiles: 1
  });

  dz.value.on('maxfilesexceeded', (exceededFile: File) => {
    dz.value?.removeAllFiles(true);
    dz.value?.addFile(exceededFile);
  });

  dz.value.on('addedfile', (addedFile: File) => {
    setSelectedFile(addedFile);
  });
};

watch(
  () => toValue(props.open),
  async isOpen => {
    if (!isOpen) return;
    await nextTick();
    initDropzone();
  }
);

watch(
  () => props.open,
  isOpen => {
    if (!isOpen) {
      dz.value?.destroy();
      dz.value = null;
    }
  }
);

onBeforeUnmount(() => {
  dz.value?.destroy();
  dz.value = null;
});

const onFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    setSelectedFile(target.files[0]);
  }
};

const uploadSelectedFile = async (selectedFile: File) => {
  try {
    submitStatus.value = 'pending';
    await uploadDocument(selectedFile);

    submitStatus.value = 'success';
    clearSelectedFile();
    await getDocuments();
    emit('success');
  } catch (error) {
    console.error('Error uploading document:', error);
    submitStatus.value = 'error';
    clearSelectedFile();
  }
};

/**
 * Upload documents to editagent
 */
async function uploadDocument(file: File) {
  const formData = new FormData();
  formData.append('file', file);

  const { response } = await callClientPortal(
    '/documents/upload',
    {
      method: 'POST',
      body: formData
    },
    { base: EDITAGENT_API_BASE }
  );

  return response;
}

/**
 * Handle document delete
 */
function openDeleteConfirm(documentId: string) {
  pendingDeleteDocumentId.value = documentId;
}

function cancelDeleteConfirm() {
  pendingDeleteDocumentId.value = null;
}

async function confirmDeleteDocument() {
  const documentId = pendingDeleteDocumentId.value;
  if (!documentId) return;

  try {
    await callClientPortal(
      `/documents/${documentId}`,
      {
        method: 'DELETE'
      },
      { base: EDITAGENT_API_BASE }
    );

    // Refresh the documents list after successful deletion
    await getDocuments();
  } catch (error) {
    console.error('Error deleting document:', error);
    alert(
      'Failed to delete document. Please ensure your network settings allow access to this site and try again or contact your network administrator.'
    );
  } finally {
    cancelDeleteConfirm();
  }
}
</script>
